import { useState } from "react";
import axios from "axios";
import { Sparkles, Paperclip, Loader2 } from "lucide-react";
import { api } from "./api.js";
import { logError } from "./logger.js";
import { useVirtualHr, type SirraContext } from "./virtualHr.js";

function mimeFromName(name: string): string {
  const ext = name.split(".").pop()?.toLowerCase();
  if (ext === "pdf") return "application/pdf";
  if (ext === "doc") return "application/msword";
  if (ext === "docx") return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (ext === "jpg" || ext === "jpeg") return "image/jpeg";
  if (ext === "png") return "image/png";
  if (ext === "gif") return "image/gif";
  if (ext === "webp") return "image/webp";
  return "application/octet-stream";
}

// Valid AI document types accepted by /api/ai/extract-document
export type AIDocType = "AADHAAR" | "PAN" | "BANK_DOCUMENT" | "RESUME" | "OFFER_LETTER" | "FORM_11";

async function initAndUpload(
  employeeId: string,
  file: File,
  docType: string,
  opts?: { name?: string | undefined; expiresAt?: string | undefined },
): Promise<{ s3Key: string; documentId: string; versionId: string }> {
  const mimeType = file.type || mimeFromName(file.name);
  const { data: initData } = await api.post<{ document: { id: string; s3Key: string }; versionId: string; uploadUrl: string }>(
    `/employees/${employeeId}/documents`,
    { name: opts?.name || file.name, type: docType, mimeType, expiresAt: opts?.expiresAt || undefined }
  );
  await axios.put(initData.uploadUrl, file, { headers: { "Content-Type": mimeType } });
  await api.patch(`/documents/${initData.document.id}/confirm-upload`);
  return { s3Key: initData.document.s3Key, documentId: initData.document.id, versionId: initData.versionId };
}

export async function parseResumePreview(file: File): Promise<Record<string, unknown>> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<{ fields: Record<string, unknown> }>(
    "/ai/parse-resume", form, { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data.fields;
}

// Add Employee parses the offer letter before the employee record exists, so this
// reads it straight from the picked file rather than from a stored document.
export async function parseOfferLetterPreview(file: File): Promise<Record<string, unknown>> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<{ fields: Record<string, unknown> }>(
    "/ai/parse-offer-letter", form, { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data.fields;
}

export async function parseBankDocPreview(file: File): Promise<Record<string, unknown>> {
  const form = new FormData();
  form.append("file", file);
  const { data } = await api.post<{ fields: Record<string, unknown> }>(
    "/ai/parse-bank", form, { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data.fields;
}

export interface SmartBitVerdict {
  decision: "ACCEPT" | "REJECT";
  code?: "WRONG_DOCUMENT" | "SUSPECTED_FAKE" | "UNREADABLE" | "UNSUPPORTED_FILE" | "IDENTITY_MISMATCH";
  message: string;
  detectedType: string;
  confidence: number;
  reasons: string[];
  qualityIssues: string[];
  suspicions: string[];
  clarity: number | null;
  /** Name / date of birth compared with the other identity document already on file. */
  crossCheck?: {
    against: "PAN" | "AADHAAR";
    mismatch: boolean;
    overridden: boolean;
    notes: string[];
  } | undefined;
  unavailable?: boolean;
  /** Virtual HR is switched off for this document: nothing was checked. */
  skipped?: boolean;
}

/** Thrown when Integer Smart Bit refuses a file; nothing has been uploaded. */
export class SmartBitRejection extends Error {
  constructor(public verdict: SmartBitVerdict) {
    super(verdict.message);
    this.name = "SmartBitRejection";
  }
}

// Integer Smart Bit: confirms the file is the document type of the slot before it is stored.
export async function verifyBeforeUpload(file: File, expectedType: string, employeeId?: string, context: SirraContext = "employee"): Promise<SmartBitVerdict> {
  const form = new FormData();
  form.append("expectedType", expectedType);
  form.append("context", context); // which on/off switch applies; sent before the file, like the other fields
  if (employeeId) form.append("employeeId", employeeId);
  form.append("file", file);
  const { data } = await api.post<SmartBitVerdict>(
    "/ai/verify-document", form, { headers: { "Content-Type": "multipart/form-data" } }
  );
  if (data.decision === "REJECT") throw new SmartBitRejection(data);
  return data;
}

/** The file was stored, but reading its details failed. `cause` is the underlying error. */
export class ExtractionFailed extends Error {
  constructor(public readonly cause: unknown) {
    super("The details could not be read from the document.");
    this.name = "ExtractionFailed";
  }
}

// Slots Integer Smart Bit gates by default. Other types (Form 11, offer letter, BGV report,
// leave attachment) are unaffected unless a caller passes `verify: true`.
const SMART_BIT_TYPES = new Set(["PAN", "AADHAAR", "OTHER", "MARKSHEET", "DEGREE", "BANK_DOCUMENT"]);

export async function uploadDocumentWithAI(
  employeeId: string,
  file: File,
  docType: string,
  aiDocType: AIDocType,
  opts?: { onVerdict?: ((v: SmartBitVerdict) => void) | undefined; onUploaded?: ((u: { versionId: string }) => void) | undefined; skip?: boolean | undefined; context?: SirraContext | undefined },
): Promise<Record<string, string | null>> {
  // Virtual HR switched off for this document: store the file, nothing is checked or read.
  if (opts?.skip) {
    const up = await initAndUpload(employeeId, file, docType);
    opts.onUploaded?.({ versionId: up.versionId });
    return {};
  }
  if (SMART_BIT_TYPES.has(docType)) {
    // The check must run even when the caller passes no options (the onboarding wizard does not):
    // keep the await out of the optional call, which would skip evaluating its arguments.
    const verdict = await verifyBeforeUpload(file, docType, employeeId, opts?.context);
    opts?.onVerdict?.(verdict);
  }
  const { s3Key, versionId } = await initAndUpload(employeeId, file, docType);
  opts?.onUploaded?.({ versionId });
  let aiData: { fields: Record<string, string | null> };
  try {
    ({ data: aiData } = await api.post<{ fields: Record<string, string | null> }>(
      "/ai/extract-document",
      { s3Key, documentType: aiDocType, context: opts?.context ?? "employee" }
    ));
  } catch (err) {
    throw new ExtractionFailed(err);
  }
  try {
    await api.patch(`/documents/versions/${versionId}/extracted-data`, { extractedData: aiData.fields });
  } catch (err) {
    // Extraction itself succeeded (the user already has prefilled fields on
    // screen) — don't block the upload flow on the persist step failing,
    // but don't let it fail silently either.
    logError("uploadDocumentWithAI.persistExtractedData", err, { employeeId, versionId });
  }
  return aiData.fields;
}

export async function uploadDocumentOnly(
  employeeId: string,
  file: File,
  docType: string,
  opts?: { name?: string | undefined; expiresAt?: string | undefined; verify?: boolean | undefined; skipVerify?: boolean | undefined; context?: SirraContext | undefined; onVerdict?: ((v: SmartBitVerdict) => void) | undefined; onUploaded?: ((u: { versionId: string }) => void) | undefined },
): Promise<{ documentId: string; versionId: string }> {
  if (!opts?.skipVerify && (opts?.verify || SMART_BIT_TYPES.has(docType))) {
    const verdict = await verifyBeforeUpload(file, docType, employeeId, opts?.context); // runs with or without options
    opts?.onVerdict?.(verdict);
  }
  const { documentId, versionId } = await initAndUpload(employeeId, file, docType, opts);
  opts?.onUploaded?.({ versionId });
  return { documentId, versionId };
}

// Attach fields a preview route already extracted to the version just uploaded, so
// the stored document carries its extraction without a second (paid) AI call — and
// without going through the S3 extract path, which cannot read Word files.
export async function persistExtractedData(versionId: string, fields: Record<string, unknown>): Promise<void> {
  try {
    await api.patch(`/documents/versions/${versionId}/extracted-data`, { extractedData: fields });
  } catch (err) {
    logError("persistExtractedData", err, { versionId });
  }
}

export async function detectUploadedDocType(s3Key: string): Promise<{ detectedType: string; confidence: number }> {
  const { data } = await api.post<{ detectedType: string; confidence: number }>(
    "/ai/detect-document-type",
    { s3Key }
  );
  return data;
}

export function AIBadge({ loading }: { loading: boolean }) {
  const { shortName } = useVirtualHr();
  if (!loading) return null;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12,
      background: "var(--color-accent-bg)", color: "var(--color-accent-fg)", borderRadius: 999, padding: "2px 10px",
    }}>
      <span style={{ fontSize: 14, display: "inline-flex" }}><Sparkles size={14} /></span> {shortName} is reading…
    </span>
  );
}

export function UploadZone({ onFile, label, accept, loading, disabled, disabledReason }: {
  onFile: (f: File) => void; label: string; accept?: string; loading?: boolean | undefined;
  /** Blocks picking and dropping files, e.g. until the data-protection notice is accepted. */
  disabled?: boolean | undefined;
  disabledReason?: string | undefined;
}) {
  const [dragging, setDragging] = useState(false);
  const inactive = !!loading || !!disabled;
  return (
    // max-w-full + the truncating label keep a long uploaded file name inside the
    // card: the zone shows the picked file's name, and those run far wider than
    // the "Choose file" placeholder it replaces.
    <label
      className="max-w-full min-w-0 overflow-hidden"
      title={loading ? undefined : disabled ? (disabledReason ?? label) : label}
      aria-disabled={disabled || undefined}
      style={{
        display: "flex", flexDirection: "row", alignItems: "center", gap: 8,
        padding: "8px 12px", height: "36px",
        border: `1px dashed ${dragging ? "var(--color-brand-500)" : "var(--color-surface-300)"}`,
        borderRadius: "var(--radius-md)", cursor: loading ? "wait" : disabled ? "not-allowed" : "pointer",
        background: dragging ? "var(--color-brand-50)" : "var(--color-surface-50)",
        transition: "all .15s", fontSize: "13px", color: "var(--color-surface-600)",
        whiteSpace: "nowrap", opacity: loading ? 0.7 : disabled ? 0.5 : 1,
      }}
      onDragOver={(e) => { e.preventDefault(); if (!inactive) setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault(); setDragging(false);
        if (!inactive && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]);
      }}
    >
      <span className="shrink-0" style={{ fontSize: 14, display: "inline-flex" }}>{loading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />}</span>
      <span className="min-w-0 truncate" style={{ fontWeight: 500 }}>{loading ? "Uploading…" : label}</span>
      <input type="file" accept={accept ?? "image/*,.pdf"} style={{ display: "none" }} disabled={inactive}
        onChange={(e) => { if (!inactive && e.target.files?.[0]) onFile(e.target.files[0]); }} />
    </label>
  );
}
